import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, InputNumber,
  Switch, Tabs, message, Popconfirm, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined,
  DragOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { TextArea } = Input;

const OPERATION_TYPES = [
  { value: 'send_auth', label: '发送授权' },
  { value: 'get_auth_code', label: '获取授权码' },
  { value: 'submit_order', label: '提交下单' },
  { value: 'query_status', label: '查询状态' },
  { value: 'query_fans', label: '查询粉丝' },
  { value: 'custom', label: '自定义操作' }
];

const AUTH_TYPES = [
  { value: 'api_key', label: 'API Key' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'signature', label: 'HMAC签名' },
  { value: 'none', label: '无认证' }
];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'];

function ApiStrategy() {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=新增
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('basic');
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [apiOps, setApiOps] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPlatform) params.platform = filterPlatform;
      if (filterStatus !== '') params.status = filterStatus;
      const res = await api.get('/api-strategies', { params });
      if (res.code === 200) setList(res.data || []);
    } catch (err) {
      message.error('加载策略列表失败');
    } finally {
      setLoading(false);
    }
  }, [filterPlatform, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  // 加载供应商和平台下拉
  useEffect(() => {
    api.get('/suppliers', { params: { pageSize: 999 } }).then(res => {
      if (res.code === 200) setSuppliers(res.data?.list || res.data || []);
    }).catch(() => {});
    api.get('/platforms').then(res => {
      if (res.code === 200) setPlatforms(res.data || []);
    }).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ auth_type: 'api_key', status: 1 });
    setWorkflowSteps([]);
    setApiOps([]);
    setActiveTab('basic');
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      platform: record.platform,
      growth_method: record.growth_method,
      team: record.team,
      supplier_id: record.supplier_id,
      auth_type: record.auth_type,
      api_key: record.api_key,
      api_secret: record.api_secret,
      price: record.price,
      status: record.status,
      remark: record.remark
    });
    setWorkflowSteps(Array.isArray(record.workflow_steps) ? [...record.workflow_steps] : []);
    setApiOps((record.apis || []).map((a, i) => ({ ...a, _key: a.id || `new_${i}` })));
    setActiveTab('basic');
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);

      // 找出供应商名称
      const supplier = suppliers.find(s => s.id === values.supplier_id);
      const body = {
        ...values,
        supplier_name: supplier?.name || null,
        workflow_steps: workflowSteps,
        apis: apiOps.map(a => ({
          id: a.id || undefined,
          operation_type: a.operation_type,
          name: a.name,
          method: a.method || 'POST',
          endpoint: a.endpoint,
          headers: a.headers || null,
          request_template: a.request_template || null,
          response_mapping: a.response_mapping || null,
          trigger_step: a.trigger_step !== undefined ? a.trigger_step : null,
          sort_order: a.sort_order || 0,
          status: a.status !== undefined ? a.status : 1,
          _delete: a._delete || false
        }))
      };

      let res;
      if (editing) {
        res = await api.put(`/api-strategies/${editing.id}`, body);
      } else {
        res = await api.post('/api-strategies', body);
      }

      if (res.code === 200) {
        message.success(editing ? '修改成功' : '新增成功');
        setModalOpen(false);
        loadData();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (err) {
      if (err.errorFields) return; // form validation
      message.error('操作失败');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/api-strategies/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        loadData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  // 流程步骤操作
  const addStep = () => {
    setWorkflowSteps([...workflowSteps, '']);
  };

  const updateStep = (index, value) => {
    const newSteps = [...workflowSteps];
    newSteps[index] = value;
    setWorkflowSteps(newSteps);
  };

  const removeStep = (index) => {
    const newSteps = workflowSteps.filter((_, i) => i !== index);
    setWorkflowSteps(newSteps);
    // 同步更新 apiOps 的 trigger_step
    setApiOps(prev => prev.map(op => {
      if (op.trigger_step === index) return { ...op, trigger_step: null };
      if (op.trigger_step > index) return { ...op, trigger_step: op.trigger_step - 1 };
      return op;
    }));
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= workflowSteps.length) return;
    const newSteps = [...workflowSteps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setWorkflowSteps(newSteps);
    // 更新 trigger_step
    setApiOps(prev => prev.map(op => {
      if (op.trigger_step === index) return { ...op, trigger_step: newIndex };
      if (op.trigger_step === newIndex) return { ...op, trigger_step: index };
      return op;
    }));
  };

  // API操作
  const addApiOp = () => {
    setApiOps([...apiOps, {
      _key: `new_${Date.now()}`,
      operation_type: 'submit_order',
      name: '',
      method: 'POST',
      endpoint: '',
      headers: null,
      request_template: null,
      response_mapping: null,
      trigger_step: null,
      sort_order: apiOps.length,
      status: 1
    }]);
  };

  const updateApiOp = (index, field, value) => {
    setApiOps(prev => {
      const newOps = [...prev];
      newOps[index] = { ...newOps[index], [field]: value };
      return newOps;
    });
  };

  const removeApiOp = (index) => {
    setApiOps(prev => {
      const op = prev[index];
      if (op.id) {
        // 已有id，标记删除
        const newOps = [...prev];
        newOps[index] = { ...newOps[index], _delete: true };
        return newOps;
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '策略名称', dataIndex: 'name', width: 180 },
    { title: '平台', dataIndex: 'platform', width: 80 },
    { title: '供应商', dataIndex: 'supplier_name', width: 140, render: v => v || '-' },
    { title: '涨粉方式', dataIndex: 'growth_method', width: 120, render: v => v || '-' },
    {
      title: 'API操作', width: 120,
      render: (_, record) => {
        const apis = record.apis || [];
        return <Badge count={apis.length} showZero overflowCount={99} />;
      }
    },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: v => v === 1 ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>
    },
    {
      title: '操作', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此策略？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Tab2: 流程步骤
  const renderWorkflowTab = () => (
    <div>
      {workflowSteps.map((step, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <span style={{ minWidth: 30, textAlign: 'center', color: '#999' }}>{index + 1}</span>
          <Button size="small" disabled={index === 0} onClick={() => moveStep(index, -1)}>↑</Button>
          <Button size="small" disabled={index === workflowSteps.length - 1} onClick={() => moveStep(index, 1)}>↓</Button>
          <Input
            value={step}
            onChange={e => updateStep(index, e.target.value)}
            placeholder={`步骤${index + 1}名称`}
            style={{ flex: 1 }}
          />
          <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeStep(index)} />
        </div>
      ))}
      <Button type="dashed" onClick={addStep} block icon={<PlusOutlined />}>添加步骤</Button>
    </div>
  );

  // Tab3: API操作配置
  const renderApiOpsTab = () => (
    <div>
      {apiOps.filter(a => !a._delete).map((op, idx) => {
        const realIndex = apiOps.indexOf(op);
        return (
          <Card
            key={op._key || op.id}
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space>
                <Tag color="blue">{OPERATION_TYPES.find(t => t.value === op.operation_type)?.label || op.operation_type}</Tag>
                <span>{op.name || '未命名'}</span>
              </Space>
            }
            extra={<Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeApiOp(realIndex)}>删除</Button>}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>操作类型</div>
                <Select
                  value={op.operation_type}
                  onChange={v => updateApiOp(realIndex, 'operation_type', v)}
                  options={OPERATION_TYPES}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>操作名称</div>
                <Input
                  value={op.name}
                  onChange={e => updateApiOp(realIndex, 'name', e.target.value)}
                  placeholder="如：提交下单"
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>请求URL</div>
                <Input
                  value={op.endpoint}
                  onChange={e => updateApiOp(realIndex, 'endpoint', e.target.value)}
                  placeholder="https://api.xxx.com/v1/order"
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>请求方法</div>
                <Select
                  value={op.method || 'POST'}
                  onChange={v => updateApiOp(realIndex, 'method', v)}
                  options={HTTP_METHODS.map(m => ({ value: m, label: m }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>触发步骤</div>
                <Select
                  value={op.trigger_step}
                  onChange={v => updateApiOp(realIndex, 'trigger_step', v)}
                  allowClear
                  placeholder="手动执行"
                  style={{ width: '100%' }}
                  options={[
                    { value: null, label: '手动执行' },
                    ...workflowSteps.map((s, i) => ({ value: i, label: `步骤${i + 1}: ${s}` }))
                  ]}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>状态</div>
                <Select
                  value={op.status}
                  onChange={v => updateApiOp(realIndex, 'status', v)}
                  options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>自定义请求头 (JSON)</div>
              <TextArea
                rows={2}
                value={op.headers ? JSON.stringify(op.headers, null, 2) : ''}
                onChange={e => {
                  try {
                    updateApiOp(realIndex, 'headers', e.target.value ? JSON.parse(e.target.value) : null);
                  } catch { /* 用户正在输入中，不更新 */ }
                }}
                placeholder='{"X-Extra": "value"}'
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>请求参数模板 (JSON, 支持{'{{xxx}}'} 占位符)</div>
              <TextArea
                rows={4}
                value={op.request_template ? JSON.stringify(op.request_template, null, 2) : ''}
                onChange={e => {
                  try {
                    updateApiOp(realIndex, 'request_template', e.target.value ? JSON.parse(e.target.value) : null);
                  } catch { /* ignore */ }
                }}
                placeholder={'{\n  "account": "{{account_name}}",\n  "platform": "{{platform}}"\n}'}
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>响应字段映射 (JSON, 点路径取值)</div>
              <TextArea
                rows={3}
                value={op.response_mapping ? JSON.stringify(op.response_mapping, null, 2) : ''}
                onChange={e => {
                  try {
                    updateApiOp(realIndex, 'response_mapping', e.target.value ? JSON.parse(e.target.value) : null);
                  } catch { /* ignore */ }
                }}
                placeholder={'{\n  "external_order_id": "data.order_id",\n  "initial_fans": "data.fans"\n}'}
              />
            </div>
          </Card>
        );
      })}
      <Button type="dashed" onClick={addApiOp} block icon={<PlusOutlined />}>添加API操作</Button>
    </div>
  );

  return (
    <div>
      <Card
        title="API策略管理"
        extra={
          <Space>
            <Select
              value={filterPlatform || undefined}
              onChange={v => setFilterPlatform(v || '')}
              allowClear
              placeholder="筛选平台"
              style={{ width: 120 }}
              options={platforms.map(p => ({ value: p.name, label: p.name }))}
            />
            <Select
              value={filterStatus !== '' ? filterStatus : undefined}
              onChange={v => setFilterStatus(v !== undefined ? v : '')}
              allowClear
              placeholder="筛选状态"
              style={{ width: 100 }}
              options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增策略</Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={false}
          size="middle"
        />
      </Card>

      <Modal
        title={editing ? '编辑策略' : '新增策略'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={confirmLoading}
        width={800}
        destroyOnClose
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'basic',
            label: '基本信息',
            children: (
              <Form form={form} layout="vertical" style={{ maxHeight: 500, overflowY: 'auto', paddingRight: 8 }}>
                <Form.Item name="name" label="策略名称" rules={[{ required: true, message: '请输入策略名称' }]}>
                  <Input placeholder="如：抖音-供应商X" />
                </Form.Item>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Form.Item name="platform" label="平台" rules={[{ required: true, message: '请选择平台' }]}>
                    <Select placeholder="选择平台" options={platforms.map(p => ({ value: p.name, label: p.name }))} />
                  </Form.Item>
                  <Form.Item name="growth_method" label="涨粉方式">
                    <Input placeholder="如：快速涨粉" />
                  </Form.Item>
                  <Form.Item name="team" label="默认小组">
                    <Input placeholder="如：一组" />
                  </Form.Item>
                  <Form.Item name="supplier_id" label="供应商">
                    <Select
                      placeholder="选择供应商"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                    />
                  </Form.Item>
                  <Form.Item name="auth_type" label="认证方式">
                    <Select options={AUTH_TYPES} />
                  </Form.Item>
                  <Form.Item name="price" label="参考单价">
                    <InputNumber prefix="¥" min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </div>
                <Form.Item name="api_key" label="API Key">
                  <Input.Password placeholder="API Key" />
                </Form.Item>
                <Form.Item name="api_secret" label="Secret">
                  <Input.Password placeholder="API Secret (签名认证时使用)" />
                </Form.Item>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
                  <Form.Item name="status" label="状态">
                    <Select options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]} />
                  </Form.Item>
                  <Form.Item name="remark" label="备注">
                    <Input placeholder="备注信息" />
                  </Form.Item>
                </div>
              </Form>
            )
          },
          {
            key: 'workflow',
            label: `流程步骤 (${workflowSteps.length})`,
            children: renderWorkflowTab()
          },
          {
            key: 'apis',
            label: `API操作 (${apiOps.filter(a => !a._delete).length})`,
            children: renderApiOpsTab()
          }
        ]} />
      </Modal>
    </div>
  );
}

export default ApiStrategy;
